import { makeRng, normalisePoint, rangeAxes } from "./mock/_mock_sampler";
import type { CampaignConfig, SamplePoint } from "./types";
import { validSymmetryPolicies } from "./symmetrySemantics";
import {
  evaluateTailorPosterior,
  proposeTailorBatch,
  type TailorRegionalEvaluation,
  type TailorRuntimeRequest,
} from "../../api/mannequin";

export type BenchmarkMethod = "tailor" | "lhs";

export type BenchmarkErrorMetric = {
  rmse: number;
  gradientRmse: number;
  sobolevH1: number;
  /** Truth range on the shared held-out probes, used only for aggregation. */
  truthRange?: number;
};

export const BENCHMARK_STALL_REGIONS = ["preStall", "transition", "postStall"] as const;
export type BenchmarkStallRegion = typeof BENCHMARK_STALL_REGIONS[number];

export type BenchmarkRegionMultipliers = Record<BenchmarkStallRegion, number>;

export const DEFAULT_BENCHMARK_REGION_MULTIPLIERS: BenchmarkRegionMultipliers = {
  preStall: 1,
  transition: 1,
  postStall: 1,
};

export type BenchmarkRegionalEvaluationConfig = {
  /** Defaults to max(1 alpha-unit, 5% of the configured alpha span). */
  transitionHalfWidth?: number;
  stallGridSize?: number;
  membershipSmoothing?: number;
  prominenceFraction?: number;
  prominenceWindow?: number;
};

export type BenchmarkRegionalErrorMetric = BenchmarkErrorMetric & {
  effectiveProbeCount: number;
};

export type BenchmarkRegionMetric = BenchmarkRegionalErrorMetric & {
  /** Raw coefficient-unit errors; the region headline is range-normalized. */
  outputs: Record<string, BenchmarkErrorMetric>;
};

export type BenchmarkMetric = BenchmarkErrorMetric & {
  coverage: number;
  outputs: Record<string, BenchmarkErrorMetric>;
  regions: Record<BenchmarkStallRegion, BenchmarkRegionMetric>;
  /** Probes whose local oracle CL curve has no primary stall inside the bounds. */
  outsideEnvelope: BenchmarkRegionMetric;
  stallClassification: {
    detectedCount: number;
    noStallInEnvelopeCount: number;
    unresolvedCount: number;
  };
  stallOnset: { min: number | null; median: number | null; max: number | null };
};

export type BenchmarkTrialPoint = {
  budget: number;
  tailor: BenchmarkMetric;
  lhs: BenchmarkMetric;
};

export type BenchmarkTrial = {
  trial: number;
  truthSeed: number;
  initialDesignSeed: number;
  tailorSeed: number;
  /** Post-seed LHS acquisition seed. Retained as samplerSeed for saved-result compatibility. */
  samplerSeed: number;
  points: BenchmarkTrialPoint[];
};

export type MetricEnvelope = {
  min: number;
  median: number;
  max: number;
};

export type BenchmarkErrorEnvelope = {
  rmse: MetricEnvelope;
  gradientRmse: MetricEnvelope;
  sobolevH1: MetricEnvelope;
};

export type BenchmarkRegionEnvelope = BenchmarkErrorEnvelope & {
  effectiveProbeCount: MetricEnvelope;
  outputs: Record<string, BenchmarkErrorEnvelope>;
};

export type BenchmarkWeightedEnvelope = BenchmarkErrorEnvelope & {
  outputs: Record<string, BenchmarkErrorEnvelope>;
};

export type BenchmarkMethodEnvelope = BenchmarkErrorEnvelope & {
  coverage: MetricEnvelope;
  outputs: Record<string, BenchmarkErrorEnvelope>;
  weighted: BenchmarkWeightedEnvelope;
  regions: Record<BenchmarkStallRegion, BenchmarkRegionEnvelope>;
  outsideEnvelope: BenchmarkRegionEnvelope;
  stallClassification: {
    detectedCount: MetricEnvelope;
    noStallInEnvelopeCount: MetricEnvelope;
    unresolvedCount: MetricEnvelope;
  };
  stallOnset: { min: MetricEnvelope; median: MetricEnvelope; max: MetricEnvelope };
};

export type BenchmarkEnvelopePoint = {
  budget: number;
  tailor: BenchmarkMethodEnvelope;
  lhs: BenchmarkMethodEnvelope;
};

export const BENCHMARK_ERROR_METRICS = ["rmse", "gradientRmse", "sobolevH1"] as const;
export type BenchmarkErrorMetricName = typeof BENCHMARK_ERROR_METRICS[number];

export type BenchmarkPairedOutcome = {
  wins: number;
  ties: number;
  losses: number;
  comparisons: number;
  /** Material Tailor wins divided by every finite paired comparison; ties stay in the denominator. */
  winRate: number;
  /** Wilson 95% interval for the material-win share, with ties counted as non-wins. */
  winRateWilson95: [number, number];
  /** Median of the per-trial LHS/Tailor error ratios, never a ratio of marginal medians. */
  medianLhsTailorRatio: number;
};

export type BenchmarkPairedWinScope = {
  aggregate: Record<BenchmarkErrorMetricName, BenchmarkPairedOutcome>;
  outputs: Record<string, Record<BenchmarkErrorMetricName, BenchmarkPairedOutcome>>;
};

export type BenchmarkPairedWinSummary = {
  /** Highest solve budget shared by every included trial. */
  budget: number | null;
  pairedTrials: number;
  equivalenceFraction: number;
  uniform: BenchmarkPairedWinScope;
  weighted: BenchmarkPairedWinScope;
};

export type BenchmarkOutput = {
  name: string;
  label: string;
  /** Fixed physical display range used to make unlike coefficients commensurate. */
  scale: number;
  /** Normalised configured acquisition weight. */
  weight: number;
};

export type MannequinBenchmarkResult = {
  output: string;
  outputs: BenchmarkOutput[];
  dimensions: number;
  repeats: number;
  trials: BenchmarkTrial[];
  envelope: BenchmarkEnvelopePoint[];
  protocol: {
    objective: "configured-multi-output-range-normalized";
    normalization: "held-out-truth-range";
    baseline: "independent-full-budget-lhs";
    outputWeights: Record<string, number>;
    priorityApplied: false;
    postDivergenceApplied: false;
    initialDesignSize: number;
    initialDesignSeed: number;
    tailorAcquisitionSeed: number;
    lhsAcquisitionSeed: number;
    regionalEvaluation: {
      axis: "alpha";
      stallOnset: "local-oracle-first-sustained-cl-turnover";
      transitionHalfWidth: number;
      stallGridSize: number;
      membershipSmoothing: number;
      prominenceFraction: number;
      prominenceWindow: number;
      prominenceReference: "local-oracle-cl-q95-q05";
      regionMultipliers: BenchmarkRegionMultipliers;
      normalization: "shared-global-truth-range";
      reduction: "membership-weighted-quadratic";
      outsideEnvelopeStatuses: ["no-stall-in-envelope", "unresolved"];
      affectsAcquisition: false;
    };
  };
  surrogate: {
    kind: "dense-gp" | "streaming-active-set-gp" | "low-rank-rff-gp";
    label: string;
    featureCount?: number;
  };
};

export type MannequinBenchmarkOptions = {
  config: CampaignConfig;
  output: string;
  truthSeed: number;
  /** Controls only post-seed LHS acquisition. */
  samplerSeed: number;
  /** Shared paired-design seed. Changing samplerSeed must not change this. */
  initialDesignSeed?: number;
  /** Independent post-seed Tailor acquisition seed. Changing samplerSeed must not change this. */
  tailorSeed?: number;
  initialDesignSize?: number;
  repeats: number;
  budget: number;
  budgets?: number[];
  trancheSize: number;
  varyTruthSeed: boolean;
  probeCount?: number;
  onProgress?: (complete: number, total: number) => void;
  /** Retained for call-site compatibility; real Tailor never uses browser RFFs. */
  featureCount?: number;
  /** Evaluation-only definition of pre/transition/post-stall regions. */
  regionalEvaluation?: BenchmarkRegionalEvaluationConfig;
  /** Initial display utility; can be reaggregated from trials without rerunning. */
  regionMultipliers?: BenchmarkRegionMultipliers;
};

const DEFAULT_INITIAL_DESIGN_SEED = 314_159;
const DEFAULT_TAILOR_ACQUISITION_SEED = 271_828;
export const MAX_MANNEQUIN_BENCHMARK_BUDGET = 10_000;
export const MAX_MANNEQUIN_PROPOSAL_BLOCK = 128;
const MAX_MANNEQUIN_BENCHMARK_PROBES = 256;

const MANNEQUIN_OUTPUTS: Record<string, { canonical: string; label: string; scale: number }> = {
  CL: { canonical: "CL", label: "CL · lift", scale: 4.7 },
  CD: { canonical: "CD", label: "CD · drag", scale: 1.4 },
  CY: { canonical: "CY", label: "CY · side force", scale: 1.0 },
  Cl: { canonical: "Cl", label: "Cl · roll", scale: 1.2 },
  C_l: { canonical: "Cl", label: "Cl · roll", scale: 1.2 },
  Cmx: { canonical: "Cl", label: "Cl · roll", scale: 1.2 },
  Cm: { canonical: "Cm", label: "Cm · pitch", scale: 2.3 },
  C_m: { canonical: "Cm", label: "Cm · pitch", scale: 2.3 },
  Cmy: { canonical: "Cm", label: "Cm · pitch", scale: 2.3 },
  Cn: { canonical: "Cn", label: "Cn · yaw", scale: 1.0 },
  C_n: { canonical: "Cn", label: "Cn · yaw", scale: 1.0 },
  Cmz: { canonical: "Cn", label: "Cn · yaw", scale: 1.0 },
};

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.trunc(value)));
}

export function benchmarkOutputs(config: CampaignConfig): BenchmarkOutput[] {
  const unique = new Map<string, { name: string; label: string; scale: number; rawWeight: number }>();
  for (const output of config.outputs) {
    const metadata = MANNEQUIN_OUTPUTS[output.name] ?? {
      canonical: output.name,
      label: output.name,
      scale: 1,
    };
    if (unique.has(metadata.canonical)) continue;
    unique.set(metadata.canonical, {
      name: metadata.canonical,
      label: metadata.label,
      scale: metadata.scale,
      rawWeight: Number.isFinite(output.weight) && output.weight >= 0 ? output.weight : 1,
    });
  }
  if (!unique.size) {
    unique.set("CL", { name: "CL", label: MANNEQUIN_OUTPUTS.CL?.label ?? "Lift coefficient", scale: 4.7, rawWeight: 1 });
  }
  const weightSum = [...unique.values()].reduce((sum, output) => sum + output.rawWeight, 0);
  const denominator = weightSum > 0 ? weightSum : unique.size;
  return [...unique.values()].map(({ rawWeight, ...output }) => ({
    ...output,
    weight: (weightSum > 0 ? rawWeight : 1) / denominator,
  }));
}

function fixedValues(config: CampaignConfig): Record<string, number> {
  return Object.fromEntries(
    config.inputs
      .filter((axis) => axis.mode === "fixed")
      .map((axis) => [axis.name, axis.fixedValue ?? 0]),
  );
}

function probePoints(config: CampaignConfig, seed: number, count: number): Array<Record<string, number>> {
  const rng = makeRng(`benchmark-probes-${seed}`, "truth-grid");
  const fixed = fixedValues(config);
  const range = rangeAxes(config.inputs);
  return Array.from({ length: count }, () => ({
    ...fixed,
    ...Object.fromEntries(range.map((axis) => {
      const min = axis.min ?? 0;
      const max = axis.max ?? 1;
      return [axis.name, min + rng() * (max - min)];
    })),
  }));
}

/**
 * Coverage is one minus the mean nearest-sample distance over a fixed probe
 * cloud. Distances are normalised per active axis and by sqrt(dimensions), so
 * the result remains in [0, 1] and is comparable from 2-D through high-D.
 */
export function normalizedCoverage(
  config: CampaignConfig,
  samples: SamplePoint[],
  probes: Array<Record<string, number>>,
): number {
  const range = rangeAxes(config.inputs);
  if (!samples.length || !probes.length || !range.length) return 0;
  const scale = Math.sqrt(range.length);
  const observed = samples.map((sample) => normalisePoint(sample.inputs, range));
  let distanceSum = 0;
  for (const probe of probes) {
    const target = normalisePoint(probe, range);
    let nearest = Number.POSITIVE_INFINITY;
    for (const point of observed) {
      let squared = 0;
      for (let axis = 0; axis < target.length; axis += 1) {
        const delta = (target[axis] ?? 0) - (point[axis] ?? 0);
        squared += delta * delta;
      }
      nearest = Math.min(nearest, Math.sqrt(squared) / scale);
    }
    distanceSum += nearest;
  }
  return Math.max(0, Math.min(1, 1 - distanceSum / probes.length));
}

function quantile(values: number[], fraction: number): number {
  const finite = values.filter(Number.isFinite).sort((left, right) => left - right);
  if (!finite.length) return Number.NaN;
  const position = (finite.length - 1) * fraction;
  const low = Math.floor(position);
  const high = Math.ceil(position);
  const weight = position - low;
  return (finite[low] ?? 0) * (1 - weight) + (finite[high] ?? 0) * weight;
}

function envelope(values: number[]): MetricEnvelope {
  return { min: quantile(values, 0), median: quantile(values, 0.5), max: quantile(values, 1) };
}

function errorEnvelope(values: BenchmarkErrorMetric[]): BenchmarkErrorEnvelope {
  return {
    rmse: envelope(values.map((value) => value.rmse)),
    gradientRmse: envelope(values.map((value) => value.gradientRmse)),
    sobolevH1: envelope(values.map((value) => value.sobolevH1)),
  };
}

function regionEnvelope(values: BenchmarkRegionMetric[]): BenchmarkRegionEnvelope {
  const outputNames = [...new Set(values.flatMap((value) => Object.keys(value.outputs)))];
  return {
    ...errorEnvelope(values),
    effectiveProbeCount: envelope(values.map((value) => value.effectiveProbeCount)),
    outputs: Object.fromEntries(outputNames.map((output) => [
      output,
      errorEnvelope(values.map((value) => value.outputs[output]!)),
    ])),
  };
}

function validateRegionMultipliers(
  multipliers: BenchmarkRegionMultipliers,
): BenchmarkRegionMultipliers {
  const result = { ...multipliers };
  if (BENCHMARK_STALL_REGIONS.some((region) => !Number.isFinite(result[region]) || result[region] < 0)) {
    throw new Error("Envelope-region multipliers must be finite and non-negative");
  }
  // Outside-envelope probes always retain neutral weight one, so all three
  // declared regions may legitimately be zero.
  return result;
}

function weightedError(
  metric: BenchmarkMetric,
  multipliers: BenchmarkRegionMultipliers,
  output?: string,
): BenchmarkErrorMetric {
  const entries = [
    ...BENCHMARK_STALL_REGIONS.map((region) => ({
      count: metric.regions[region].effectiveProbeCount,
      multiplier: multipliers[region],
      error: output ? metric.regions[region].outputs[output]! : metric.regions[region],
    })),
    {
      count: metric.outsideEnvelope.effectiveProbeCount,
      multiplier: 1,
      error: output ? metric.outsideEnvelope.outputs[output]! : metric.outsideEnvelope,
    },
  ];
  const reduce = (key: "rmse" | "gradientRmse" | "sobolevH1"): number => {
    let numerator = 0;
    let denominator = 0;
    for (const entry of entries) {
      const effectiveWeight = entry.count * entry.multiplier;
      const value = entry.error?.[key];
      if (!(effectiveWeight > 0) || !Number.isFinite(value)) continue;
      numerator += effectiveWeight * value ** 2;
      denominator += effectiveWeight;
    }
    return denominator > 0 ? Math.sqrt(numerator / denominator) : Number.NaN;
  };
  return {
    rmse: reduce("rmse"),
    gradientRmse: reduce("gradientRmse"),
    sobolevH1: reduce("sobolevH1"),
    ...(output && metric.outputs[output]?.truthRange != null
      ? { truthRange: metric.outputs[output]!.truthRange }
      : {}),
  };
}

function aggregateMethod(
  points: BenchmarkTrialPoint[],
  method: BenchmarkMethod,
  multipliers: BenchmarkRegionMultipliers,
): BenchmarkMethodEnvelope {
  const outputNames = [...new Set(points.flatMap((point) => Object.keys(point[method].outputs)))];
  const weighted = points.map((point) => weightedError(point[method], multipliers));
  return {
    rmse: envelope(points.map((point) => point[method].rmse)),
    gradientRmse: envelope(points.map((point) => point[method].gradientRmse)),
    sobolevH1: envelope(points.map((point) => point[method].sobolevH1)),
    coverage: envelope(points.map((point) => point[method].coverage)),
    weighted: {
      ...errorEnvelope(weighted),
      outputs: Object.fromEntries(outputNames.map((output) => [
        output,
        errorEnvelope(points.map((point) => weightedError(point[method], multipliers, output))),
      ])),
    },
    regions: Object.fromEntries(BENCHMARK_STALL_REGIONS.map((region) => [
      region,
      regionEnvelope(points.map((point) => point[method].regions[region])),
    ])) as Record<BenchmarkStallRegion, BenchmarkRegionEnvelope>,
    outsideEnvelope: regionEnvelope(points.map((point) => point[method].outsideEnvelope)),
    stallClassification: {
      detectedCount: envelope(points.map((point) => point[method].stallClassification.detectedCount)),
      noStallInEnvelopeCount: envelope(points.map((point) => point[method].stallClassification.noStallInEnvelopeCount)),
      unresolvedCount: envelope(points.map((point) => point[method].stallClassification.unresolvedCount)),
    },
    stallOnset: {
      min: envelope(points.map((point) => point[method].stallOnset.min ?? Number.NaN)),
      median: envelope(points.map((point) => point[method].stallOnset.median ?? Number.NaN)),
      max: envelope(points.map((point) => point[method].stallOnset.max ?? Number.NaN)),
    },
    outputs: Object.fromEntries(outputNames.map((output) => [output, {
      rmse: envelope(points.map((point) => point[method].outputs[output]?.rmse ?? Number.NaN)),
      gradientRmse: envelope(points.map((point) => point[method].outputs[output]?.gradientRmse ?? Number.NaN)),
      sobolevH1: envelope(points.map((point) => point[method].outputs[output]?.sobolevH1 ?? Number.NaN)),
    }])),
  };
}

export function aggregateBenchmarkTrials(
  trials: BenchmarkTrial[],
  regionMultipliers: BenchmarkRegionMultipliers = DEFAULT_BENCHMARK_REGION_MULTIPLIERS,
): BenchmarkEnvelopePoint[] {
  const multipliers = validateRegionMultipliers(regionMultipliers);
  const budgets = [...new Set(trials.flatMap((trial) => trial.points.map((point) => point.budget)))].sort((a, b) => a - b);
  return budgets.map((budget) => {
    const points = trials.flatMap((trial) => trial.points.filter((point) => point.budget === budget));
    return {
      budget,
      tailor: aggregateMethod(points, "tailor", multipliers),
      lhs: aggregateMethod(points, "lhs", multipliers),
    };
  });
}

const EMPTY_PAIRED_OUTCOME: BenchmarkPairedOutcome = {
  wins: 0,
  ties: 0,
  losses: 0,
  comparisons: 0,
  winRate: Number.NaN,
  winRateWilson95: [Number.NaN, Number.NaN],
  medianLhsTailorRatio: Number.NaN,
};

function pairedRatio(lhsError: number, tailorError: number): number {
  if (lhsError === 0 && tailorError === 0) return 1;
  if (tailorError === 0) return Number.POSITIVE_INFINITY;
  return lhsError / tailorError;
}

function ratioMedian(values: number[]): number {
  if (!values.length) return Number.NaN;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2) return sorted[middle]!;
  const lower = sorted[middle - 1]!;
  const upper = sorted[middle]!;
  if (!Number.isFinite(lower) || !Number.isFinite(upper)) return upper;
  return (lower + upper) / 2;
}

function wilson95(wins: number, comparisons: number): [number, number] {
  if (!(comparisons > 0)) return [Number.NaN, Number.NaN];
  const z = 1.959963984540054;
  const zSquared = z * z;
  const observed = wins / comparisons;
  const denominator = 1 + zSquared / comparisons;
  const centre = (observed + zSquared / (2 * comparisons)) / denominator;
  const halfWidth = z * Math.sqrt(
    (observed * (1 - observed) + zSquared / (4 * comparisons)) / comparisons,
  ) / denominator;
  return [Math.max(0, centre - halfWidth), Math.min(1, centre + halfWidth)];
}

function pairedOutcome(
  values: Array<{ tailor: number; lhs: number }>,
  equivalenceFraction: number,
): BenchmarkPairedOutcome {
  const finite = values.filter(({ tailor, lhs }) => Number.isFinite(tailor)
    && Number.isFinite(lhs)
    && tailor >= 0
    && lhs >= 0);
  if (!finite.length) return { ...EMPTY_PAIRED_OUTCOME, winRateWilson95: [...EMPTY_PAIRED_OUTCOME.winRateWilson95] };
  let wins = 0;
  let ties = 0;
  let losses = 0;
  const ratios = finite.map(({ tailor, lhs }) => pairedRatio(lhs, tailor));
  const winThreshold = 1 + equivalenceFraction;
  const lossThreshold = 1 / winThreshold;
  for (const ratio of ratios) {
    if (ratio >= winThreshold) wins += 1;
    else if (ratio <= lossThreshold) losses += 1;
    else ties += 1;
  }
  const comparisons = finite.length;
  return {
    wins,
    ties,
    losses,
    comparisons,
    winRate: wins / comparisons,
    winRateWilson95: wilson95(wins, comparisons),
    medianLhsTailorRatio: ratioMedian(ratios),
  };
}

function pairedWinScope(
  points: BenchmarkTrialPoint[],
  multipliers: BenchmarkRegionMultipliers,
  equivalenceFraction: number,
): BenchmarkPairedWinScope {
  const outputNames = [...new Set(points.flatMap((point) => [
    ...Object.keys(point.tailor.outputs),
    ...Object.keys(point.lhs.outputs),
  ]))];
  const valuesFor = (metric: BenchmarkErrorMetricName, output?: string) => points.map((point) => {
    const tailor = weightedError(point.tailor, multipliers, output);
    const lhs = weightedError(point.lhs, multipliers, output);
    return {
      tailor: tailor?.[metric] ?? Number.NaN,
      lhs: lhs?.[metric] ?? Number.NaN,
    };
  });
  return {
    aggregate: Object.fromEntries(BENCHMARK_ERROR_METRICS.map((metric) => [
      metric,
      pairedOutcome(valuesFor(metric), equivalenceFraction),
    ])) as Record<BenchmarkErrorMetricName, BenchmarkPairedOutcome>,
    outputs: Object.fromEntries(outputNames.map((output) => [
      output,
      Object.fromEntries(BENCHMARK_ERROR_METRICS.map((metric) => [
        metric,
        pairedOutcome(valuesFor(metric, output), equivalenceFraction),
      ])) as Record<BenchmarkErrorMetricName, BenchmarkPairedOutcome>,
    ])),
  };
}

/**
 * Compare Tailor and LHS only within matched trials at one shared final budget.
 * No outputs, error definitions, or point-count checkpoints are pooled into a
 * synthetic headline. The primary UI statistic is the envelope-weighted,
 * all-output range-normalized Sobolev H1 cell;
 * the remaining response/metric cells are reported separately as diagnostics.
 */
export function summarizeBenchmarkPairedWins(
  trials: BenchmarkTrial[],
  regionMultipliers: BenchmarkRegionMultipliers = DEFAULT_BENCHMARK_REGION_MULTIPLIERS,
  equivalenceFraction = 0.02,
): BenchmarkPairedWinSummary {
  const multipliers = validateRegionMultipliers(regionMultipliers);
  if (!Number.isFinite(equivalenceFraction) || equivalenceFraction < 0 || equivalenceFraction >= 1) {
    throw new Error("Benchmark equivalence fraction must be finite and in [0, 1)");
  }
  const sharedBudgets = trials.length
    ? [...new Set(trials[0]!.points.map((point) => point.budget))].filter((budget) => (
        trials.every((trial) => trial.points.some((point) => point.budget === budget))
      ))
    : [];
  const budget = sharedBudgets.length ? Math.max(...sharedBudgets) : null;
  const points = budget == null
    ? []
    : trials.flatMap((trial) => trial.points.find((point) => point.budget === budget) ?? []);
  return {
    budget,
    pairedTrials: points.length,
    equivalenceFraction,
    uniform: pairedWinScope(points, DEFAULT_BENCHMARK_REGION_MULTIPLIERS, equivalenceFraction),
    weighted: pairedWinScope(points, multipliers, equivalenceFraction),
  };
}

function cloneConfig(
  config: CampaignConfig,
  engine: "tailor-mannequin" | "lhs-mannequin",
  truthSeed: number,
): CampaignConfig {
  return {
    ...config,
    inputs: config.inputs.map((axis) => ({ ...axis })),
    outputs: config.outputs.map((output) => ({ ...output })),
    sampler: { ...config.sampler, engine, mannequinSeed: truthSeed },
  };
}

function realRuntimeRequest(
  config: CampaignConfig,
  engine: "tailor" | "lhs",
  truthSeed: number,
  acquisitionSeed: number,
  initialDesignSeed: number,
  initialDesignSize: number,
  iteration: number,
  observed: SamplePoint[],
  batchSize: number,
): TailorRuntimeRequest {
  const outputs = benchmarkOutputs(config);
  const symmetries = validSymmetryPolicies(
    config.inputs,
    config.sampler?.symmetries ?? (config.sampler?.symmetry ? [config.sampler.symmetry] : []),
  );
  return {
    vehicle: config.sampler?.mannequinFamily ?? "cessna_like",
    seed: truthSeed,
    method: "spera",
    // Adversarial pathologies deliberately break the clean aircraft
    // reflection. Never pair them with a declared symmetry prior.
    pathologies: symmetries.length > 0 ? "none" : "sampled",
    // Every stochastic input is already explicit in the declared truth/design/
    // acquisition seeds.  Do not leak the benchmark repeat index into the
    // runtime stream: repeats re-seed LHS, while fixed-truth Tailor must remain
    // the same paired reference in every trial.
    campaign_key: `benchmark-${engine}-truth-${truthSeed}-design-${initialDesignSeed}-acq-${acquisitionSeed}`,
    iteration,
    engine,
    inputs: config.inputs.map((axis) => ({
      name: axis.name,
      mode: axis.mode,
      ...(axis.min != null ? { min: axis.min } : {}),
      ...(axis.max != null ? { max: axis.max } : {}),
      ...(axis.fixedValue != null ? { fixed_value: axis.fixedValue } : {}),
    })),
    // Surface selection is presentation-only. Tailor always acquires against
    // every configured aerodynamic response and its configured relative weight.
    outputs: outputs.map((output) => output.name),
    output_weights: Object.fromEntries(outputs.map((output) => [output.name, output.weight])),
    expert_weights: config.sampler?.tailorWeights
      ? Object.fromEntries(Object.entries(config.sampler.tailorWeights).filter((entry): entry is [string, number] => Number.isFinite(entry[1])))
      : undefined,
    observations: observed.map((point) => ({ inputs: point.inputs })),
    batch_size: batchSize,
    initial_design_size: initialDesignSize,
    initial_design_seed: initialDesignSeed,
    acquisition_seed: acquisitionSeed,
    symmetries,
    // This is a uniform global benchmark. User priority/post-divergence policies
    // target a different objective, so neither is silently applied here.
    priority: null,
    post_divergence: null,
    candidates_per_step: 600,
  };
}

function trialSeed(base: number, trial: number): number {
  return (clampInteger(base, 0, 1_000_000) + trial) % 1_000_001;
}

function proposalBatchSize(
  observedCount: number,
  checkpoint: number,
  trancheSize: number,
  initialDesignSize: number,
): number {
  const checkpointRemaining = Math.max(0, checkpoint - observedCount);
  if (observedCount === 0) return Math.min(checkpointRemaining, initialDesignSize);
  if (observedCount < initialDesignSize) {
    // Stop exactly on the full-design boundary. This keeps a later adaptive
    // tranche from being ambiguously mixed with the remaining paired LHS seed.
    return Math.min(checkpointRemaining, trancheSize, initialDesignSize - observedCount);
  }
  return Math.min(checkpointRemaining, trancheSize);
}

type EvaluationPayload = {
  rmse: number;
  gradient_rmse?: number;
  gradient_seminorm?: number;
  sobolev_h1?: number;
  truth_range?: number;
  normalized_rmse?: number;
  normalized_sobolev_h1?: number;
};

type ResolvedRegionalEvaluation = {
  transitionHalfWidth: number;
  stallGridSize: number;
  membershipSmoothing: number;
  prominenceFraction: number;
  prominenceWindow: number;
};

function resolveRegionalEvaluation(
  config: CampaignConfig,
  requested: BenchmarkRegionalEvaluationConfig | undefined,
): ResolvedRegionalEvaluation {
  const alpha = config.inputs.find((axis) => axis.name === "alpha" && axis.mode === "range");
  if (alpha?.min == null || alpha.max == null || alpha.max <= alpha.min) {
    throw new Error("Pre/post-stall evaluation requires alpha to be a varying benchmark input");
  }
  const resolved = {
    transitionHalfWidth: requested?.transitionHalfWidth ?? Math.max(1, 0.05 * (alpha.max - alpha.min)),
    stallGridSize: clampInteger(requested?.stallGridSize ?? 121, 61, 401),
    membershipSmoothing: requested?.membershipSmoothing ?? 0.25,
    prominenceFraction: requested?.prominenceFraction ?? 0.01,
    prominenceWindow: requested?.prominenceWindow ?? 4,
  };
  if (!Number.isFinite(resolved.transitionHalfWidth) || resolved.transitionHalfWidth < 0) {
    throw new Error("Stall transition half-width must be finite and non-negative");
  }
  if (!Number.isFinite(resolved.membershipSmoothing) || resolved.membershipSmoothing < 0) {
    throw new Error("Stall-region membership smoothing must be finite and non-negative");
  }
  if (!(resolved.prominenceFraction > 0 && resolved.prominenceFraction <= 1)) {
    throw new Error("Stall prominence fraction must be in (0, 1]");
  }
  if (!(resolved.prominenceWindow > 0)) {
    throw new Error("Stall prominence window must be positive");
  }
  if ((alpha.max - alpha.min) / (resolved.stallGridSize - 1) > resolved.prominenceWindow / 3) {
    throw new Error("Stall grid is too coarse for the configured prominence window");
  }
  return resolved;
}

function evaluationError(evaluation: EvaluationPayload): BenchmarkErrorMetric {
  const gradientRmse = evaluation.gradient_rmse ?? Number.NaN;
  const sobolevH1 = evaluation.sobolev_h1
    ?? (Number.isFinite(gradientRmse) ? Math.hypot(evaluation.rmse, gradientRmse) : Number.NaN);
  return {
    rmse: evaluation.rmse,
    gradientRmse,
    sobolevH1,
    ...(Number.isFinite(evaluation.truth_range) && Number(evaluation.truth_range) > 0
      ? { truthRange: Number(evaluation.truth_range) }
      : {}),
  };
}

function regionalEvaluationError(
  evaluation: TailorRegionalEvaluation["per_output"][string]["regions"]["pre_stall"],
): BenchmarkRegionalErrorMetric {
  const rmse = evaluation.rmse ?? Number.NaN;
  const gradientRmse = evaluation.gradient_rmse ?? Number.NaN;
  const sobolevH1 = evaluation.sobolev_h1 ?? Number.NaN;
  return {
    rmse,
    gradientRmse,
    sobolevH1,
    effectiveProbeCount: evaluation.effective_probe_count,
    ...(Number.isFinite(evaluation.truth_range) && evaluation.truth_range > 0
      ? { truthRange: evaluation.truth_range }
      : {}),
  };
}

type BenchmarkEvaluation = {
  errors: Record<string, BenchmarkErrorMetric>;
  regions: Record<BenchmarkStallRegion, BenchmarkRegionMetric>;
  outsideEnvelope: BenchmarkRegionMetric;
  stallClassification: BenchmarkMetric["stallClassification"];
  stallOnset: BenchmarkMetric["stallOnset"];
};

const API_REGION_NAMES: Record<BenchmarkStallRegion, "pre_stall" | "transition" | "post_stall"> = {
  preStall: "pre_stall",
  transition: "transition",
  postStall: "post_stall",
};

async function evaluateOutputs(
  request: TailorRuntimeRequest,
  outputs: BenchmarkOutput[],
  probes: Array<Record<string, number>>,
  regionalConfig: ResolvedRegionalEvaluation,
): Promise<BenchmarkEvaluation> {
  const regional_evaluation = {
    transition_half_width: regionalConfig.transitionHalfWidth,
    stall_grid_size: regionalConfig.stallGridSize,
    membership_smoothing: regionalConfig.membershipSmoothing,
    prominence_fraction: regionalConfig.prominenceFraction,
    prominence_window: regionalConfig.prominenceWindow,
  };
  const primaryOutput = outputs[0]!;
  const primary = await evaluateTailorPosterior({
    ...request,
    output: primaryOutput.name,
    probes,
    regional_evaluation,
  });
  const perOutput = (primary as typeof primary & {
    per_output?: Record<string, EvaluationPayload>;
  }).per_output;
  const payloads: Record<string, EvaluationPayload> = {
    [primaryOutput.name]: primary,
    ...(perOutput ?? {}),
  };
  const missing = outputs.filter((output) => payloads[output.name] == null);
  const fallbacks = await Promise.all(missing.map(async (output) => [
    output.name,
    await evaluateTailorPosterior({ ...request, output: output.name, probes, regional_evaluation }),
  ] as const));
  for (const [name, evaluation] of fallbacks) payloads[name] = evaluation;
  const errors = Object.fromEntries(
    outputs.map((output) => [output.name, evaluationError(payloads[output.name]!)]),
  );
  const regionalPayloads: Record<string, TailorRegionalEvaluation["per_output"][string]> = {
    ...(primary.regional?.per_output ?? {}),
  };
  for (const [, evaluation] of fallbacks) {
    Object.assign(regionalPayloads, evaluation.regional?.per_output ?? {});
  }
  if (!primary.regional || outputs.some((output) => regionalPayloads[output.name] == null)) {
    throw new Error("Tailor runtime did not return the requested oracle stall-region evaluation");
  }
  const buildRegion = (
    apiRegion: "pre_stall" | "transition" | "post_stall" | "outside_envelope",
  ): BenchmarkRegionMetric => {
    const outputErrors = Object.fromEntries(outputs.map((output) => {
      const payload = apiRegion === "outside_envelope"
        ? regionalPayloads[output.name]!.outside_envelope
        : regionalPayloads[output.name]!.regions[apiRegion];
      return [output.name, regionalEvaluationError(payload)];
    }));
    const first = outputErrors[outputs[0]!.name]!;
    return {
      rmse: normalizedAggregate(outputs, outputErrors, "rmse"),
      gradientRmse: normalizedAggregate(outputs, outputErrors, "gradientRmse"),
      sobolevH1: normalizedAggregate(outputs, outputErrors, "sobolevH1"),
      effectiveProbeCount: first.effectiveProbeCount,
      outputs: outputErrors,
    };
  };
  return {
    errors,
    regions: Object.fromEntries(BENCHMARK_STALL_REGIONS.map((region) => [
      region,
      buildRegion(API_REGION_NAMES[region]),
    ])) as Record<BenchmarkStallRegion, BenchmarkRegionMetric>,
    outsideEnvelope: buildRegion("outside_envelope"),
    stallClassification: {
      detectedCount: probes.length - primary.regional.outside_envelope_count,
      noStallInEnvelopeCount: primary.regional.no_stall_in_envelope_count,
      unresolvedCount: primary.regional.unresolved_count,
    },
    stallOnset: primary.regional.stall_onset,
  };
}

function normalizedAggregate(
  outputs: BenchmarkOutput[],
  errors: Record<string, BenchmarkErrorMetric>,
  metric: "rmse" | "gradientRmse" | "sobolevH1",
): number {
  let weightedSquaredError = 0;
  let finiteWeight = 0;
  for (const output of outputs) {
    const value = errors[output.name]?.[metric];
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    const scale = errors[output.name]?.truthRange ?? output.scale;
    weightedSquaredError += output.weight * (value / scale) ** 2;
    finiteWeight += output.weight;
  }
  return finiteWeight > 0 ? Math.sqrt(weightedSquaredError / finiteWeight) : Number.NaN;
}

function benchmarkMetric(
  evaluation: BenchmarkEvaluation,
  outputs: BenchmarkOutput[],
  coverage: number,
): BenchmarkMetric {
  const { errors } = evaluation;
  return {
    rmse: normalizedAggregate(outputs, errors, "rmse"),
    gradientRmse: normalizedAggregate(outputs, errors, "gradientRmse"),
    sobolevH1: normalizedAggregate(outputs, errors, "sobolevH1"),
    coverage,
    outputs: errors,
    regions: evaluation.regions,
    outsideEnvelope: evaluation.outsideEnvelope,
    stallClassification: evaluation.stallClassification,
    stallOnset: evaluation.stallOnset,
  };
}

function realObservations(
  method: BenchmarkMethod,
  trial: number,
  iteration: number,
  proposals: Awaited<ReturnType<typeof proposeTailorBatch>>["proposals"],
): SamplePoint[] {
  return proposals.map((proposal, index) => ({
    id: `benchmark-${method}-${trial}-${iteration}-${index}`,
    iter: iteration,
    batchId: `benchmark-${method}-${trial}-${iteration}`,
    status: "done",
    source: "sampler",
    inputs: proposal.inputs,
    outputs: proposal.truth_outputs,
    truthOutputs: proposal.truth_outputs,
    predicted: proposal.predicted,
  }));
}

async function runRealTrial(
  options: MannequinBenchmarkOptions,
  trial: number,
  checkpoints: number[],
  probeCount: number,
): Promise<BenchmarkTrial> {
  const truthSeed = options.truthSeed + (options.varyTruthSeed ? trial : 0);
  // Repeats isolate the requested LHS reseed envelope. Tailor's seed design
  // and candidate stream stay fixed; the optional aircraft toggle is the only
  // additional source of variation.
  const initialDesignSeed = clampInteger(options.initialDesignSeed ?? DEFAULT_INITIAL_DESIGN_SEED, 0, 1_000_000);
  const tailorSeed = clampInteger(options.tailorSeed ?? DEFAULT_TAILOR_ACQUISITION_SEED, 0, 1_000_000);
  const samplerSeed = trialSeed(options.samplerSeed, trial);
  const dimensions = rangeAxes(options.config.inputs).length;
  const initialDesignSize = clampInteger(options.initialDesignSize ?? Math.max(16, dimensions * 8), 1, 128);
  const tailorConfig = cloneConfig(options.config, "tailor-mannequin", truthSeed);
  const lhsConfig = cloneConfig(options.config, "lhs-mannequin", truthSeed);
  const outputs = benchmarkOutputs(options.config);
  const regionalConfig = resolveRegionalEvaluation(options.config, options.regionalEvaluation);
  const probes = probePoints(tailorConfig, truthSeed + 65_537, probeCount);
  let tailor: SamplePoint[] = [];
  let iteration = 0;
  const points: BenchmarkTrialPoint[] = [];

  for (const checkpoint of checkpoints) {
    while (tailor.length < checkpoint) {
      iteration += 1;
      const batchSize = proposalBatchSize(tailor.length, checkpoint, options.trancheSize, initialDesignSize);
      const response = await proposeTailorBatch(realRuntimeRequest(
        tailorConfig, "tailor", truthSeed, tailorSeed, initialDesignSeed, initialDesignSize,
        iteration, tailor, batchSize,
      ));
      if (response.proposals.length !== batchSize) {
        throw new Error(
          `Tailor returned ${response.proposals.length.toLocaleString()} of ${batchSize.toLocaleString()} requested points; ` +
          `the ${checkpoint.toLocaleString()}-point benchmark was not completed.`,
        );
      }
      tailor.push(...realObservations("tailor", trial, iteration, response.proposals));
    }
    // The scientific end-result baseline is a genuine, independently reseeded
    // N-point Latin hypercube at each checkpoint. It is intentionally rebuilt
    // from zero: unioning API tranches creates only a blockwise/sliced design,
    // not an N-stratum LHS, and can bias the headline comparison.
    // Stream one logical N-stratum LHS in API-safe blocks. Repeating the same
    // N-point initial-design seed with the accumulated prefix returns the next
    // part of that same full design; it is not a union of unrelated mini-LHSs.
    let lhs: SamplePoint[] = [];
    let lhsIteration = 0;
    while (lhs.length < checkpoint) {
      lhsIteration += 1;
      const batchSize = Math.min(MAX_MANNEQUIN_PROPOSAL_BLOCK, checkpoint - lhs.length);
      const response = await proposeTailorBatch(realRuntimeRequest(
        lhsConfig, "lhs", truthSeed, samplerSeed, samplerSeed, checkpoint,
        lhsIteration, lhs, batchSize,
      ));
      if (response.proposals.length !== batchSize) {
        throw new Error(
          `LHS returned ${response.proposals.length.toLocaleString()} of ${batchSize.toLocaleString()} requested points; ` +
          `the ${checkpoint.toLocaleString()}-point benchmark was not completed.`,
        );
      }
      lhs.push(...realObservations("lhs", trial, lhsIteration, response.proposals));
    }
    if (tailor.length !== checkpoint || lhs.length !== checkpoint) {
      throw new Error(
        `Benchmark point-count mismatch at N=${checkpoint.toLocaleString()} ` +
        `(Tailor ${tailor.length.toLocaleString()}, LHS ${lhs.length.toLocaleString()}).`,
      );
    }
    const tailorEvaluationRequest = realRuntimeRequest(
      tailorConfig, "tailor", truthSeed, tailorSeed, initialDesignSeed, initialDesignSize,
      iteration, tailor, 1,
    );
    const lhsEvaluationRequest = realRuntimeRequest(
      lhsConfig, "lhs", truthSeed, samplerSeed, samplerSeed, checkpoint,
      checkpoint, lhs, 1,
    );
    const [tailorErrors, lhsErrors] = await Promise.all([
      evaluateOutputs(tailorEvaluationRequest, outputs, probes, regionalConfig),
      evaluateOutputs(lhsEvaluationRequest, outputs, probes, regionalConfig),
    ]);
    points.push({
      budget: checkpoint,
      tailor: benchmarkMetric(tailorErrors, outputs, normalizedCoverage(tailorConfig, tailor, probes)),
      lhs: benchmarkMetric(lhsErrors, outputs, normalizedCoverage(lhsConfig, lhs, probes)),
    });
  }
  return { trial: trial + 1, truthSeed, initialDesignSeed, tailorSeed, samplerSeed, points };
}

export async function runMannequinBenchmark(options: MannequinBenchmarkOptions): Promise<MannequinBenchmarkResult> {
  const repeats = clampInteger(options.repeats, 1, 30);
  const trancheSize = clampInteger(options.trancheSize, 1, MAX_MANNEQUIN_PROPOSAL_BLOCK);
  const requestedCheckpoints = (options.budgets?.length ? options.budgets : [options.budget])
    .map((value) => clampInteger(value, 1, MAX_MANNEQUIN_BENCHMARK_BUDGET));
  const checkpoints = [...new Set(requestedCheckpoints)].sort((left, right) => left - right);
  const maxBudget = checkpoints[checkpoints.length - 1] ?? options.budget;
  const budget = clampInteger(options.budget, 1, MAX_MANNEQUIN_BENCHMARK_BUDGET);
  const defaultProbeCount = Math.max(96, Math.ceil(Math.sqrt(maxBudget) * 2));
  const probeCount = clampInteger(
    options.probeCount ?? defaultProbeCount,
    32,
    MAX_MANNEQUIN_BENCHMARK_PROBES,
  );
  const dimensions = rangeAxes(options.config.inputs).length;
  const outputs = benchmarkOutputs(options.config);
  const regionalConfig = resolveRegionalEvaluation(options.config, options.regionalEvaluation);
  const regionMultipliers = validateRegionMultipliers(
    options.regionMultipliers ?? DEFAULT_BENCHMARK_REGION_MULTIPLIERS,
  );
  const initialDesignSize = clampInteger(options.initialDesignSize ?? Math.max(16, dimensions * 8), 1, 128);
  const trials: BenchmarkTrial[] = [];
  for (let trial = 0; trial < repeats; trial += 1) {
    // A normal run compares the exact selected N once. Multi-N work is opt-in
    // through `budgets`; it must not multiply a 10k-point run by every transport
    // block merely because the API is streamed in bounded chunks.
    const trialCheckpoints = options.budgets?.length ? checkpoints : [budget];
    trials.push(await runRealTrial({ ...options, trancheSize }, trial, trialCheckpoints, probeCount));
    options.onProgress?.(trial + 1, repeats);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  return {
    output: options.output,
    outputs,
    dimensions,
    repeats,
    trials,
    envelope: aggregateBenchmarkTrials(trials, regionMultipliers),
    protocol: {
      objective: "configured-multi-output-range-normalized",
      normalization: "held-out-truth-range",
      baseline: "independent-full-budget-lhs",
      outputWeights: Object.fromEntries(outputs.map((output) => [output.name, output.weight])),
      priorityApplied: false,
      postDivergenceApplied: false,
      initialDesignSize,
      initialDesignSeed: clampInteger(options.initialDesignSeed ?? DEFAULT_INITIAL_DESIGN_SEED, 0, 1_000_000),
      tailorAcquisitionSeed: clampInteger(options.tailorSeed ?? DEFAULT_TAILOR_ACQUISITION_SEED, 0, 1_000_000),
      lhsAcquisitionSeed: clampInteger(options.samplerSeed, 0, 1_000_000),
      regionalEvaluation: {
        axis: "alpha",
        stallOnset: "local-oracle-first-sustained-cl-turnover",
        transitionHalfWidth: regionalConfig.transitionHalfWidth,
        stallGridSize: regionalConfig.stallGridSize,
        membershipSmoothing: regionalConfig.membershipSmoothing,
        prominenceFraction: regionalConfig.prominenceFraction,
        prominenceWindow: regionalConfig.prominenceWindow,
        prominenceReference: "local-oracle-cl-q95-q05",
        regionMultipliers,
        normalization: "shared-global-truth-range",
        reduction: "membership-weighted-quadratic",
        outsideEnvelopeStatuses: ["no-stall-in-envelope", "unresolved"],
        affectsAcquisition: false,
      },
    },
    surrogate: maxBudget > 128
      ? {
          kind: "streaming-active-set-gp",
          label: "Python ARD Matérn residual GP + bounded 128-point response-aware active set",
        }
      : {
          kind: "dense-gp",
          label: "Python ARD Matérn residual GP + configured five-expert acquisition policy",
        },
  };
}
